import NoDataFound from "@/app/components/common/warning/NoDataFound";
import DrawerSearchInput from "@/app/models/drawer/DrawerSearchInput";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { maskString } from "@/app/utils/helpers";
import { RadioButton } from "@progress/kendo-react-inputs";
import { memo, useEffect, useRef, useState } from "react";
import "./DirectDebitScreen.scss";
import type { DirectDebitAccount } from "@/app/types/accounts";
import { useDirectDebitAccount } from "./apiHooks/useDirectDebitAccounts";
import { Loader } from "@progress/kendo-react-indicators";
import { useDebounce } from "@/app/hooks/useDebounce";

interface DirectDebitAccountListProp {
  selectedAccountId: string;
  onSelect: (value: DirectDebitAccount) => void;
  placeholder: string;
}

const DirectDebitAccountList = memo(
  ({
    selectedAccountId,
    onSelect,
    placeholder,
  }: DirectDebitAccountListProp) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [search, setSearch] = useState<string>("");
    const searchDebounce = useDebounce(search, 500);
    const { directDebitAccounts, hasMore } = useDirectDebitAccount({
      page,
      setLoading,
      search: searchDebounce,
    });
    

    const [visibleIds, setVisibleIds] = useState<string[]>([]);
    const loaderRef = useRef<HTMLDivElement | null>(null);

    const toggleVisibility = (id: string) => {
      if (visibleIds.includes(id)) {
        const filterArray = visibleIds.filter((x: string) => x !== id);
        setVisibleIds(filterArray);
      } else {
        setVisibleIds((prev) => [...prev, id]);
      }
    };

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !loading && hasMore) {
            setPage((prev) => prev + 1);
          }
        },
        { threshold: 1 },
      );

      if (loaderRef.current) {
        observer.observe(loaderRef.current);
      }

      return () => {
        if (loaderRef.current) {
          observer.unobserve(loaderRef.current);
        }
      };
    }, [loading, hasMore]);

    const handleSearch = (value: string) => {
      setSearch(value);
    };

    return (
      <div className="gx-direct-debit-account-list">
        <DrawerSearchInput
          searchQuery={search}
          placeholder={placeholder}
          onSearchChange={handleSearch}
        />
        <div className="gx-direct-debit-account-item-view">
          {/* Loader while API is fetching */}
          {loading && directDebitAccounts.length === 0 && (
            <div className="my-[2rem] flex justify-center">
              <Loader size="medium" type="converging-spinner" />
            </div>
          )}

          {/* Show list when data exists */}
          {!loading && directDebitAccounts.length > 0 && (
            <>
              {directDebitAccounts.map((account: DirectDebitAccount) => {
                const isVisible = visibleIds.includes(account.cbsAccountId);
                return (
                  <button
                    key={account.cbsAccountId}
                    className="gx-direct-debit-account-item"
                    onClick={() => onSelect(account)}
                  >
                    <div className="gx-direct-debit-account-item-info">
                      <img
                        src={`${ICON_BASE_PATH}beneficiarybankname.svg`}
                        alt="bank_logo"
                      />
                      <div className="gx-direct-debit-account-details">
                        <p className="gx-direct-debit-account-name gx-p14-sb">
                          {account.accountName}
                        </p>
                        <div className="gx-direct-debit-account-card">
                          <p className="gx-p12-md">
                            {isVisible
                              ? account.accountNo
                              : maskString(account.accountNo)}
                          </p>
                          <button
                            className="gx-direct-debit-account-visibility"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleVisibility(account.cbsAccountId);
                            }}
                          >
                            <img
                              src={
                                isVisible
                                  ? `${ICON_BASE_PATH}eyeopen.svg`
                                  : `${ICON_BASE_PATH}eyeclose.svg`
                              }
                              alt="Show"
                              className="gx-eye-icon"
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <RadioButton
                        checked={selectedAccountId === account.cbsAccountId}
                      />
                    </div>
                  </button>
                );
              })}
            </>
          )}

          {/* Show NoData when empty and not loading */}
          {!loading && directDebitAccounts.length === 0 && (
            <div className="gx-no-direct-debit-account">
              <NoDataFound
                iconType={search ? 2 : 1}
                message={
                  search
                    ? "No direct debits match your search"
                    : "No direct debits found for this account"
                }
              />
            </div>
          )}

          {/* Loader at bottom for infinite scroll */}
          {directDebitAccounts.length > 0 && (
            <div ref={loaderRef} className="my-[2rem] flex justify-center">
              {loading && <Loader size="medium" type="converging-spinner" />}
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default DirectDebitAccountList;
